import { ELEMENT_BASE, resolveElementBox } from 'lib/sceneElementBox'
import { LiveSceneSnapshot, SceneElement } from 'types/api'

export const SCENE_FADE_MS = 500

export const CONTENT_HOLD_MAX_MS = 1000

export const SETTLE_SLACK_MS = 50

export type SceneTransition = 'CUT' | 'FADE'

const HELD_KINDS: ReadonlySet<SceneElement['elementKind']> = new Set(['MERCH', 'MERCH_TICKER', 'TICKER_BAR'])

const byCodeUnit = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)

export const presenceKeyOf = (el: Pick<SceneElement, 'elementKind' | 'elementId'>): string =>
  `${el.elementKind}:${el.elementId}`

export interface RenderSignature {
  x: number
  y: number
  glyphScale: number
  layoutWidth: number
  clipH: number | null
}

export const renderSignatureOf = (el: SceneElement): RenderSignature => {
  const box = resolveElementBox(el)
  const clipped = el.elementKind === 'CHAT' || el.elementKind === 'EMOTEWALL'
  return {
    x: el.x,
    y: el.y,
    glyphScale: box.glyphScale,
    layoutWidth: box.layoutWidth,
    clipH: clipped
      ? (el.h == null ? ELEMENT_BASE[el.elementKind].h : Math.max(1, Math.round(el.h / box.glyphScale)))
      : null
  }
}

export const sameSignature = (a: RenderSignature, b: RenderSignature): boolean =>
  a.x === b.x && a.y === b.y && a.glyphScale === b.glyphScale &&
  a.layoutWidth === b.layoutWidth && a.clipH === b.clipH

export const takeIdOf = (liveScene: Pick<LiveSceneSnapshot, 'sceneId' | 'takenAt'> | null | undefined): string | null =>
  liveScene == null ? null : `${liveScene.sceneId}|${liveScene.takenAt}`

export const renderableOf = (elements: readonly SceneElement[]): SceneElement[] =>
  elements.filter((el) => el.visible && el.elementKind !== 'CAMERA_CONTROL')

export const glideTransitionOf = (ms: number): string =>
  `left ${ms}ms ease-in-out, top ${ms}ms ease-in-out, transform ${ms}ms ease-in-out`

export const clipGlideTransitionOf = (ms: number): string =>
  `width ${ms}ms ease-in-out, height ${ms}ms ease-in-out`

export type PresencePhase = 'holding' | 'entering' | 'steady' | 'exiting'

export interface PresenceEntry {
  key: string
  el: SceneElement
  phase: PresencePhase
  gliding: boolean
  opacityMs: number
  fadeIn: boolean
  since: number
}

export type TimerAction = 'settle' | 'hold'

export interface TimerRequest {
  key: string
  action: TimerAction | null
  delayMs: number
}

export interface PresenceStep {
  entries: PresenceEntry[]
  timers: TimerRequest[]
}

export interface TakeDiff {
  entered: string[]
  exited: string[]
  kept: string[]
  moved: string[]
  restacked: string[]
}

export interface PresenceItem {
  key: string
  el: SceneElement
  opacity: 0 | 1
  opacityMs: number
  glideMs: number
  holding: boolean
  fadeIn: boolean
}

const sorted = (keys: string[]): string[] => [...keys].sort(byCodeUnit)

const isLive = (e: PresenceEntry): boolean => e.phase !== 'exiting'

const diffAgainst = (entries: readonly PresenceEntry[], next: readonly SceneElement[]): TakeDiff => {
  const byKey = new Map(entries.map((e) => [e.key, e]))
  const nextKeys = new Set(next.map(presenceKeyOf))
  const entered: string[] = []
  const kept: string[] = []
  const moved: string[] = []
  const restacked: string[] = []
  for (const el of next) {
    const key = presenceKeyOf(el)
    const e = byKey.get(key)
    if (e == null || !isLive(e)) {
      entered.push(key)
      continue
    }
    if (sameSignature(renderSignatureOf(e.el), renderSignatureOf(el))) kept.push(key)
    else moved.push(key)
    if (e.el.z !== el.z) restacked.push(key)
  }
  const exited = entries.filter((e) => isLive(e) && !nextKeys.has(e.key)).map((e) => e.key)
  return {
    entered: sorted(entered),
    exited: sorted(exited),
    kept: sorted(kept),
    moved: sorted(moved),
    restacked: sorted(restacked)
  }
}

const steadyEntryOf = (el: SceneElement): PresenceEntry => ({
  key: presenceKeyOf(el),
  el,
  phase: 'steady',
  gliding: false,
  opacityMs: 0,
  fadeIn: false,
  since: 0
})

export const diffTake = (prevEls: readonly SceneElement[], nextEls: readonly SceneElement[]): TakeDiff =>
  diffAgainst(renderableOf(prevEls).map(steadyEntryOf), renderableOf(nextEls))

export const isEmptyDiff = (d: TakeDiff): boolean =>
  d.entered.length === 0 && d.exited.length === 0 && d.moved.length === 0 && d.restacked.length === 0

const insertByZ = (list: PresenceEntry[], entry: PresenceEntry): void => {
  const at = list.findIndex((e) => e.el.z > entry.el.z)
  if (at === -1) list.push(entry)
  else list.splice(at, 0, entry)
}

const cancelTimer = (key: string): TimerRequest => ({ key, action: null, delayMs: 0 })

export const applyTake = (
  entries: readonly PresenceEntry[],
  nextEls: readonly SceneElement[],
  transition: SceneTransition,
  now: number,
  fadeMs: number = SCENE_FADE_MS
): PresenceStep & { diff: TakeDiff } => {
  const next = renderableOf(nextEls)
  const diff = diffAgainst(entries, next)
  if (isEmptyDiff(diff)) return { entries: entries as PresenceEntry[], timers: [], diff }
  const fade = transition === 'FADE' && fadeMs > 0
  const settleMs = fadeMs + SETTLE_SLACK_MS
  const nextByKey = new Map(next.map((el) => [presenceKeyOf(el), el]))
  const timers: TimerRequest[] = []
  const out: PresenceEntry[] = []
  for (const e of entries) {
    const n = nextByKey.get(e.key)
    if (n == null) {
      if (e.phase === 'exiting') {
        if (fade) out.push(e)
        else timers.push(cancelTimer(e.key))
        continue
      }
      if (!fade || e.phase === 'holding') {
        timers.push(cancelTimer(e.key))
        continue
      }
      out.push({ ...e, phase: 'exiting', opacityMs: fadeMs, since: now })
      timers.push({ key: e.key, action: 'settle', delayMs: settleMs })
      continue
    }
    const moved = !sameSignature(renderSignatureOf(e.el), renderSignatureOf(n))
    if (!fade) {
      const settled = e.phase === 'steady' && !e.gliding && e.opacityMs === 0
      out.push(settled && e.el === n
        ? e
        : { ...e, el: n, phase: 'steady', gliding: false, opacityMs: 0, fadeIn: false, since: settled ? e.since : now })
      if (!settled) timers.push(cancelTimer(e.key))
      continue
    }
    if (e.phase === 'exiting') {
      out.push({ ...e, el: n, phase: 'entering', gliding: moved, opacityMs: fadeMs, since: now })
      timers.push({ key: e.key, action: 'settle', delayMs: settleMs })
      continue
    }
    if (e.phase === 'holding' || !moved) {
      out.push(e.el === n ? e : { ...e, el: n })
      continue
    }
    out.push({ ...e, el: n, gliding: true, since: now })
    timers.push({ key: e.key, action: 'settle', delayMs: settleMs })
  }
  const known = new Set(entries.map((e) => e.key))
  const added = next
    .map((el, index) => ({ el, index }))
    .filter(({ el }) => !known.has(presenceKeyOf(el)))
    .sort((a, b) => (a.el.z !== b.el.z ? a.el.z - b.el.z : a.index - b.index))
  for (const { el } of added) {
    const key = presenceKeyOf(el)
    if (!fade) {
      insertByZ(out, { ...steadyEntryOf(el), since: now })
      continue
    }
    const held = HELD_KINDS.has(el.elementKind)
    insertByZ(out, {
      key,
      el,
      phase: held ? 'holding' : 'entering',
      gliding: false,
      opacityMs: fadeMs,
      fadeIn: true,
      since: now
    })
    timers.push(held
      ? { key, action: 'hold', delayMs: CONTENT_HOLD_MAX_MS }
      : { key, action: 'settle', delayMs: settleMs })
  }
  return { entries: out, timers, diff }
}

export const pruneVanished = (entries: readonly PresenceEntry[], nextEls: readonly SceneElement[]): PresenceStep => {
  const keys = new Set(renderableOf(nextEls).map(presenceKeyOf))
  const gone = entries.filter((e) => isLive(e) && !keys.has(e.key))
  if (gone.length === 0) return { entries: entries as PresenceEntry[], timers: [] }
  return {
    entries: entries.filter((e) => !gone.includes(e)),
    timers: gone.map((e) => cancelTimer(e.key))
  }
}

const replaceAt = (entries: readonly PresenceEntry[], index: number, entry: PresenceEntry | null): PresenceEntry[] => {
  const out = [...entries]
  if (entry == null) out.splice(index, 1)
  else out[index] = entry
  return out
}

export const settle = (entries: readonly PresenceEntry[], key: string, now: number): PresenceStep => {
  const index = entries.findIndex((e) => e.key === key)
  if (index === -1) return { entries: entries as PresenceEntry[], timers: [] }
  const e = entries[index]
  if (e.phase === 'exiting') return { entries: replaceAt(entries, index, null), timers: [] }
  if (e.phase === 'entering' || e.gliding) {
    return { entries: replaceAt(entries, index, { ...e, phase: 'steady', gliding: false, since: now }), timers: [] }
  }
  return { entries: entries as PresenceEntry[], timers: [] }
}

const releaseHold = (entries: readonly PresenceEntry[], key: string, now: number, fadeMs: number): PresenceStep => {
  const index = entries.findIndex((e) => e.key === key)
  if (index === -1 || entries[index].phase !== 'holding') return { entries: entries as PresenceEntry[], timers: [] }
  return {
    entries: replaceAt(entries, index, { ...entries[index], phase: 'entering', opacityMs: fadeMs, since: now }),
    timers: [{ key, action: 'settle', delayMs: fadeMs + SETTLE_SLACK_MS }]
  }
}

export const contentReady = (entries: readonly PresenceEntry[], key: string, now: number, fadeMs: number = SCENE_FADE_MS): PresenceStep =>
  releaseHold(entries, key, now, fadeMs)

export const holdExpired = (entries: readonly PresenceEntry[], key: string, now: number, fadeMs: number = SCENE_FADE_MS): PresenceStep =>
  releaseHold(entries, key, now, fadeMs)

export const timerFor = (e: PresenceEntry, fadeMs: number = SCENE_FADE_MS): TimerRequest | null => {
  if (e.phase === 'holding') return { key: e.key, action: 'hold', delayMs: CONTENT_HOLD_MAX_MS }
  if (e.phase === 'entering' || e.phase === 'exiting' || e.gliding) {
    return { key: e.key, action: 'settle', delayMs: fadeMs + SETTLE_SLACK_MS }
  }
  return null
}

export const itemsOf = (entries: readonly PresenceEntry[], fadeMs: number = SCENE_FADE_MS): PresenceItem[] =>
  entries.map((e) => ({
    key: e.key,
    el: e.el,
    opacity: e.phase === 'exiting' || e.phase === 'holding' ? 0 : 1,
    opacityMs: e.opacityMs,
    glideMs: e.gliding ? fadeMs : 0,
    holding: e.phase === 'holding',
    fadeIn: e.fadeIn
  }))
